import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { useMutation } from '@apollo/client/react';
import { Notify } from 'notiflix';
import { SurveyCreatorComponent, SurveyCreator } from 'survey-creator-react';
import 'survey-creator-core/survey-creator-core.min.css';
import { UPDATE_COURSE_FEEDBACK_SCHEMA } from 'helper/graphql/mutations';

const CREATOR_OPTIONS = {
	showLogicTab: false,
	showTranslationTab: false,
	showThemeTab: false,
	showJSONEditorTab: false,
	isAutoSave: true,
};

const CourseFeedbackFormModal = ({ show, onHide, course, onSaved }) => {
	const [creator] = useState(() => {
		const c = new SurveyCreator(CREATOR_OPTIONS);
		c.isAutoSave = true;
		return c;
	});

	useEffect(() => {
		if (!show) return;
		if (course?.feedbackFormSchema) {
			try {
				const raw = course.feedbackFormSchema;
				creator.JSON = typeof raw === 'string' ? JSON.parse(raw) : raw;
			} catch {
				creator.JSON = {};
			}
		} else {
			creator.JSON = {};
		}
	}, [course, show]);

	useLayoutEffect(() => {
		const banner = document.querySelector('div.svc-creator__banner');
		if (banner) banner.style.display = 'none';
	});

	const [updateSchema, { loading }] = useMutation(UPDATE_COURSE_FEEDBACK_SCHEMA);

	const handleSave = async () => {
		try {
			const schema = creator.JSON;
			const hasSchema = schema && Object.keys(schema).length > 0;
			await updateSchema({
				variables: {
					guid: course.guid,
					feedbackFormSchema: hasSchema ? JSON.stringify(schema) : null,
				},
			});
			Notify.success('Feedback form saved!');
			onSaved?.();
			onHide();
		} catch (err) {
			Notify.failure(err?.message || 'Failed to save feedback form.');
		}
	};

	return (
		<Modal show={show} onHide={onHide} size="xl" centered>
			<Modal.Header closeButton className="cffm-header">
				<Modal.Title className="cffm-title">
					<i className="fe fe-file-text me-2"></i>
					Course Feedback Form
					{course?.title && (
						<span className="cffm-course-name ms-2">— {course.title}</span>
					)}
				</Modal.Title>
			</Modal.Header>

			<Modal.Body className="p-0">
				<div className="cffm-hint">
					<i className="fe fe-info me-2"></i>
					Design the post-course feedback form. Students submit this after completing the course to receive their e-certificate.
				</div>
				<div className="cffm-creator-wrap">
					<SurveyCreatorComponent creator={creator} />
				</div>
			</Modal.Body>

			<Modal.Footer className="cffm-footer">
				<Button variant="light" onClick={onHide} disabled={loading} className="cffm-cancel-btn">
					Cancel
				</Button>
				<Button className="cffm-save-btn" onClick={handleSave} disabled={loading}>
					{loading ? (
						<><Spinner size="sm" className="me-2" />Saving…</>
					) : (
						<><i className="fe fe-save me-2"></i>Save Feedback Form</>
					)}
				</Button>
			</Modal.Footer>

			<style>{`
				.cffm-header {
					background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
					border-bottom: 2px solid #e2e8f0;
					padding: 1.25rem 1.5rem;
				}
				.cffm-title { font-size: 1.0625rem; font-weight: 700; color: #1e293b; display: flex; align-items: center; flex-wrap: wrap; }
				.cffm-course-name { font-weight: 400; color: #64748b; font-size: 0.9rem; }
				.cffm-hint {
					padding: 0.625rem 1rem;
					background: rgba(29,158,176,0.06);
					border-bottom: 1px solid #e2e8f0;
					font-size: 0.8125rem;
					color: #475569;
					display: flex;
					align-items: center;
				}
				.cffm-creator-wrap { height: 600px; display: flex; flex-direction: column; }
				.cffm-creator-wrap .svc-creator { flex: 1; min-height: 0; }
				.cffm-footer { border-top: 1px solid #e2e8f0; padding: 1rem 1.5rem; gap: 0.75rem; }
				.cffm-cancel-btn { border: 1.5px solid #e2e8f0; color: #64748b; font-weight: 600; border-radius: 8px; }
				.cffm-save-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					border: none; color: white; font-weight: 600; border-radius: 8px;
					padding: 0.5rem 1.5rem; display: flex; align-items: center;
				}
				.cffm-save-btn:hover:not(:disabled) { opacity: 0.9; color: white; background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%); }
				.cffm-save-btn:disabled { opacity: 0.65; background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%); color: white; }
			`}</style>
		</Modal>
	);
};

export default CourseFeedbackFormModal;
